import { differenceInYears } from 'date-fns';

/**
 * Whole years lived, or null when the date cannot yield a sensible age.
 *
 * `differenceInYears` rather than subtracting the years: a child born in
 * December is still the younger age until December comes round, and the naive
 * subtraction gets that wrong for most of the year. A negative result means a
 * mistyped future date — the caller sees the date itself and no age, rather
 * than "-1 tuổi".
 */
export function ageOf(dateOfBirth: string): number | null {
  const years = differenceInYears(new Date(), new Date(dateOfBirth));
  return Number.isFinite(years) && years >= 0 ? years : null;
}

/**
 * Short labels for a list of students shown together — a session's roster.
 *
 * Vietnamese addresses people by the last word of their name, so that is the
 * default. But a roster is exactly where those collide: two sisters in one
 * slot really are `Nguyễn Cát Hồng Ân` and `Nguyễn Cát Thiên Ân`, and
 * rendering `Ân, Ân` is worse than the wall of full names it replaced.
 *
 * So the widening is scoped to the collision: whoever shares a given name with
 * someone else in THIS list falls back to their last two words — the common
 * Vietnamese short form — and everyone else stays one word. The cost is paid
 * only where the ambiguity is.
 *
 * Pure, and pure on purpose: no DB column, no API field. `studentName` is the
 * full name and the given name is its last word, which is exactly what the
 * `givenName` trigger computes for sorting. Two jobs, one derivation.
 */
export function displayNames(fullNames: string[]): string[] {
  const words = fullNames.map((n) => n.trim().split(/\s+/).filter(Boolean));
  const given = words.map((w) => w.at(-1) ?? '');

  const shared = new Set<string>();
  const seen = new Set<string>();
  for (const g of given) {
    if (seen.has(g)) shared.add(g);
    seen.add(g);
  }

  return words.map((w, i) => {
    if (w.length === 0) return '';
    const g = given[i]!;
    return shared.has(g) ? w.slice(-2).join(' ') : g;
  });
}

/**
 * The last two words of a name — how Vietnamese people are most often
 * addressed in full-ish form ("Nguyễn Cát Hồng Ân" -> "Hồng Ân").
 *
 * Used where there is room for it and the extra word is worth the space: the
 * session list's roster line. `displayNames` is the other choice, for places
 * that are tight enough to want one word and therefore have to handle two
 * students sharing it.
 */
export function lastTwoWords(fullName: string): string {
  const words = fullName.trim().split(/\s+/).filter(Boolean);
  return words.slice(-2).join(' ');
}

/**
 * The given name — last word of a Vietnamese full name, and what the
 * `givenName` DB trigger (apps/api/src/services/givenName.ts) stores for
 * server-side sorting (`orderBy: [{ givenName: 'asc' }, ...]` on
 * GET /students and GET /clients). Lists fetched by other endpoints — e.g.
 * this session's roster picker, which comes back `createdAt desc` with no
 * `givenName` field at all — don't have that column to sort by, so they
 * derive it client-side with this and order with `localeCompare(a, b, 'vi')`.
 */
export function givenNameOf(fullName: string): string {
  return fullName.trim().split(/\s+/).filter(Boolean).at(-1) ?? '';
}
